<template>
  <div class="enter-community-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('enterCommunity.page.title')" :subtitle="$t('enterCommunity.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="showHcUse">
          {{ $t('enterCommunity.commercialProcess') }}
        </el-button>
      </template>
    </ds-page-header>

    <template v-if="showPage === 'myCommunity'">
      <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
      <div class="ds-panel">
        <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
          @update:density="density = $event" @refresh="fetchCommunityList">
          <template #left>
            <span class="ds-panel__title">{{ $t('enterCommunity.myCommunity') }}</span>
          </template>
        </ds-toolbar>

        <ds-data-table :data="communityList" :loading="loading" :error="hasError" :error-message="errorMessage"
          :density="density" border :empty-description="$t('enterCommunity.page.empty')" @retry="fetchCommunityList">
          <el-table-column prop="provName" :label="$t('enterCommunity.province')" align="center" width="110" />
          <el-table-column prop="cityName" :label="$t('enterCommunity.city')" align="center" width="110" />
          <el-table-column prop="areaName" :label="$t('enterCommunity.district')" align="center" width="120" />
          <el-table-column prop="name" :label="$t('enterCommunity.communityName')" align="left" min-width="160"
            show-overflow-tooltip />
          <el-table-column prop="communityId" :label="$t('enterCommunity.communityCode')" align="center" width="170">
            <template slot-scope="scope">
              <span class="ds-num">{{ scope.row.communityId || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="tel" :label="$t('enterCommunity.servicePhone')" align="center" width="130">
            <template slot-scope="scope">
              <span class="ds-num">{{ scope.row.tel || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="communityArea" :label="$t('enterCommunity.area')" align="right" width="110">
            <template slot-scope="scope">
              <span class="ds-num">{{ formatNumber(scope.row.communityArea) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="startTime" :label="$t('enterCommunity.startTime')" align="center" width="110">
            <template slot-scope="scope">
              <span class="ds-num">{{ scope.row.startTime || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="endTime" :label="$t('enterCommunity.endTime')" align="center" width="110">
            <template slot-scope="scope">
              <span class="ds-num">{{ scope.row.endTime || '-' }}</span>
            </template>
          </el-table-column>
          <!-- 状态 Tag：颜色仅作辅助，始终带文字（WCAG 1.4.1） -->
          <el-table-column :label="$t('enterCommunity.status')" align="center" width="120">
            <template slot-scope="{ row }">
              <ds-status-tag :type="resolveAuditState(row)" :label="resolveAuditStateLabel(row)" />
            </template>
          </el-table-column>
          <el-table-column :label="$t('enterCommunity.operation')" align="center" width="100" fixed="right">
            <template slot-scope="{ row }">
              <el-button v-if="row.auditStatusCd === '1100'" type="text" size="mini" @click="openEditDialog(row)">
                {{ $t('enterCommunity.modify') }}
              </el-button>
            </template>
          </el-table-column>
        </ds-data-table>

        <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
          :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handlePageChange">
          <template #summary>
            <span class="ds-tip-text">{{ $t('enterCommunity.page.tip') }}</span>
          </template>
        </ds-pagination-bar>
      </div>
    </template>

    <!-- 商用流程说明页 -->
    <template v-else>
      <div class="ds-panel ds-hc-use-panel">
        <div class="ds-hc-use-panel__header">
          <span>{{ $t('enterCommunity.commercialProcess') }}</span>
          <el-button size="mini" icon="el-icon-close" @click="goBack">
            {{ $t('enterCommunity.back') }}
          </el-button>
        </div>
        <div class="ds-hc-use-panel__body">
          <img src="/img/hc_use.png" alt="Commercial Process">
        </div>
      </div>
    </template>

    <edit-community-area ref="editDialog" @success="fetchCommunityList" />
  </div>
</template>

<script>
import { listMyCommunity } from '@/api/community/enterCommunityApi'
import EditCommunityArea from '@/components/community/editCommunityArea.vue'
import { formatNumber } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'EnterCommunityList',
  components: {
    EditCommunityArea,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      showPage: 'myCommunity',
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      communityList: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      statusMap: {
        '1000': this.$t('enterCommunity.statusOptions.1000'),
        '1100': this.$t('enterCommunity.statusOptions.1100'),
        '1200': this.$t('enterCommunity.statusOptions.1200')
      }
    }
  },
  mounted() {
    this.fetchCommunityList()
  },
  methods: {
    async fetchCommunityList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          communityId: this.getCommunityId()
        }
        const data = await listMyCommunity(params)
        this.communityList = Array.isArray(data) ? data : []
        this.pagination.total = this.communityList.length
      } catch (error) {
        console.error('获取我的小区列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('enterCommunity.page.loadFailed')
        this.$message.error(error.message || this.$t('enterCommunity.fetchError'))
      } finally {
        this.loading = false
      }
    },
    openEditDialog(community) {
      this.$refs.editDialog.open(community)
    },
    showHcUse() {
      this.showPage = 'hcUse'
    },
    goBack() {
      this.showPage = 'myCommunity'
    },
    handlePageChange(page) {
      this.pagination.current = page
      this.fetchCommunityList()
    },
    handleSizeChange(size) {
      this.pagination.size = size
      this.fetchCommunityList()
    },
    /**
     * 小区面积格式化（只改展示，不改原值）
     */
    formatNumber(value) {
      return formatNumber(value)
    },
    /**
     * 入驻审核状态语义类型：1000 待审核 / 1100 审核完成 / 1200 审核拒绝
     */
    resolveAuditState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.auditStatusCd, {
        domain: 'enterCommunity',
        text: this.statusMap[row.auditStatusCd] || ''
      }).type
    },
    /**
     * 入驻审核状态文案：优先使用本地 i18n 映射，缺失时回落状态原文
     */
    resolveAuditStateLabel(row) {
      if (!row) return '-'
      return this.statusMap[row.auditStatusCd] || row.auditStatusCd || '-'
    }
  }
}
</script>

<style lang="scss" scoped>
.enter-community-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 商用流程说明面板 */
.ds-hc-use-panel {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
    background: var(--ds-gray-50);
    border-bottom: 1px solid var(--ds-gray-300);
  }

  &__body {
    padding: var(--ds-spacing-lg);
    text-align: center;

    img {
      max-width: 100%;
    }
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
